<!--
 * @Author: Tahiti
 * @Date: 2026-01-27
 * @Description: 灵感推荐组件 - 用于视频生成页面的右侧灵感推荐面板
-->
<template>
    <div class="inspiration-recommend">
        <!-- 标题区域 -->
        <div class="inspiration-header">
            <img :src="afflatus" alt="灵感推荐" class="inspiration-icon" />
            <span class="inspiration-title">{{ t('common.inspirationRecommend') }}</span>
        </div>

        <!-- Loading 状态 -->
        <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 灵感列表 -->
        <div v-else class="inspiration-list">
            <div
                v-for="item in displayWorks"
                :key="item.ins_id"
                class="inspiration-item"
                @click="handleClickItem(item)"
            >
                <!-- 缩略图 -->
                <div class="inspiration-thumbnail">
                    <img
                        v-if="item.param.pic_urls && item.param.pic_urls[0]"
                        :src="item.param.pic_urls[0]"
                        :alt="item.param.prompt"
                        class="thumbnail-image"
                    />
                    <video
                        v-else-if="item.output"
                        :src="item.output"
                        :poster="item.param.pic_urls?.[0] || ''"
                        class="thumbnail-video"
                        muted
                        loop
                        playsinline
                    ></video>
                </div>

                <!-- 信息区域 -->
                <div class="inspiration-info">
                    <div class="inspiration-type">{{ getTypeText(item.type) }}</div>
                    <div class="inspiration-prompt">{{ getShortPrompt(item.param.prompt) }}</div>
                </div>
            </div>
        </div>

        <!-- 查看更多按钮 -->
        <div v-if="!loading" class="inspiration-footer">
            <button class="view-more-btn" @click="handleViewMore">
                {{ t('common.viewMoreWorks') }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { useToolsHooks } from '@/utils/useHooks'
import { getInspirationWorksList, type InspirationWorkItem } from '@/api/api'
import { prepareAllowedInspirationNavigationItem } from '~/utils/compliance-models'
import afflatus from '~/assets/images/common/afflatus.png'

const { t, locale } = useI18n()
const { handleMenuLinkClick } = useToolsHooks()

interface WorkItem {
    ins_id: string
    relate_his_id: number
    type: 'TEXT_TO_VIDEO' | 'IMG_TO_VIDEO' | string
    type_name: string
    user: {
        user_name: string
        avatar: string
    }
    param: {
        aspect_ratio: string
        model: string
        prompt: string
        output_num: number
        pic_urls?: string[]
        resolution: string
        t: number
        [key: string]: any
    }
    likes_num: number
    has_like: boolean
    output: string
    output_with_watermark: string
    create_time: number
    status: string
}

// Props
interface Props {
    modelKey?: string
    filterType?: string  // 过滤类型：'IMG_TO_VIDEO' 或 'TEXT_TO_VIDEO'
}

const props = withDefaults(defineProps<Props>(), {
    modelKey: '',
    filterType: '',
})

// 作品列表数据（从接口获取）
const worksData = ref<WorkItem[]>([])
const loading = ref(true)

// 获取作品列表
const fetchWorks = async () => {
    try {
        loading.value = true
        const response = await getInspirationWorksList()
        
        if (response.status === 200 && response.data) {
            // 将接口返回的数据转换为组件使用的格式
            worksData.value = response.data.map((item: InspirationWorkItem) => ({
                ins_id: String(item.id), // 将 id 转换为 ins_id
                relate_his_id: item.relate_his_id,
                type: item.type,
                type_name: item.type_name,
                user: item.user,
                param: item.param,
                likes_num: item.likes_num,
                has_like: item.has_like,
                output: item.output,
                output_with_watermark: item.output_with_watermark,
                create_time: item.create_time,
                status: item.status,
            }))
        }
    } catch (error) {
        console.error('获取灵感作品列表失败:', error)
    } finally {
        loading.value = false
    }
}

// 组件挂载时获取数据
onMounted(() => {
    fetchWorks()
})

// 根据过滤类型显示作品
const displayWorks = computed(() => {
    if (!props.filterType) {
        return worksData.value
    }
    return worksData.value.filter(item => item.type === props.filterType)
})

// 获取短提示词
const getShortPrompt = (prompt: string) => {
    if (!prompt) return ''
    return prompt.length > 60 ? prompt.substring(0, 60) + '...' : prompt
}

// 获取类型文本
const getTypeText = (type: string) => {
    if (type === 'TEXT_TO_VIDEO') {
        return t('common.textToVideo')
    } else if (type === 'IMG_TO_VIDEO') {
        return t('common.imageToVideo')
    } else if (type === 'IMG_TO_IMG') {
        return t('common.imageToImage')
    }
    return type
}

// 点击灵感项 - 使用作品数据本身的模型
const handleClickItem = (item: WorkItem) => {
    if (typeof window === 'undefined') return

    const timestamp = Date.now()

    const { item: navigationItem, defaultModel } =
        prepareAllowedInspirationNavigationItem(item)
    const defaultModelQuery = defaultModel
        ? `defaultModel=${encodeURIComponent(defaultModel)}&`
        : ''

    // 保存数据到 localStorage
    localStorage.setItem('inspirationData', JSON.stringify(navigationItem))

    // 根据类型跳转到通用页面（localStorage 中已保存正确的 model）
    if (item.type === 'TEXT_TO_VIDEO') {
        const path = `/text-to-video?${defaultModelQuery}customType=inspiration&t=${timestamp}`
        handleMenuLinkClick(path, new MouseEvent('click'))
    } else if (item.type === 'IMG_TO_VIDEO') {
        const path = `/image-to-video?${defaultModelQuery}customType=inspiration&t=${timestamp}`
        handleMenuLinkClick(path, new MouseEvent('click'))
    } else if (item.type === 'IMG_TO_IMG') {
        const path = `/image-to-image?${defaultModelQuery}customType=inspiration&t=${timestamp}`
        handleMenuLinkClick(path, new MouseEvent('click'))
    }
}

// 查看更多 - 滚动到 CommunityWorks 组件
const handleViewMore = () => {
    // 查找 CommunityWorks 组件的 section 元素
    const communityWorksSection = document.querySelector('.community-works-section')
    if (communityWorksSection) {
        // 平滑滚动到 CommunityWorks 组件顶部
        communityWorksSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        })
    }
}
</script>

<style lang="less" scoped>
.inspiration-recommend {
    display: flex;
    flex-direction: column;
    height: 350px;
    overflow: hidden;
}

.inspiration-header {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.inspiration-icon {
    width: 20px;
    height: 20px;
}

.inspiration-title {
    font-family: 'Roboto', sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 1.2em;
    color: #ffffff;
}

.inspiration-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;
    overflow-y: auto;
    
    &::-webkit-scrollbar {
        width: 4px;
    }
    
    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 2px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 2px;
        
        &:hover {
            background: rgba(255, 255, 255, 0.3);
        }
    }
}

.inspiration-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 14px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.05);
        transform: translateX(2px);
    }
}

.inspiration-thumbnail {
    width: 58px;
    height: 58px;
    flex-shrink: 0;
    border-radius: 8px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.05);
}

.thumbnail-image,
.thumbnail-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.inspiration-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.inspiration-type {
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 1.5em;
    color: #ffffff;
}

.inspiration-prompt {
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 400;
    font-size: 11px;
    line-height: 1.5em;
    color: rgba(255, 255, 255, 0.7);
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
}

.inspiration-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
    height: 61px;
}

.view-more-btn {
    width: 100%;
    max-width: 283px;
    height: 41px;
    padding: 13px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    font-family: 'Roboto', sans-serif;
    font-weight: 400;
    font-size: 11px;
    line-height: 1.2em;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.15);
        border-color: rgba(255, 255, 255, 0.2);
    }

    &:active {
        transform: scale(0.98);
    }
}

// Loading 状态样式
.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    padding: 40px 20px;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.1);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.loading-text {
    margin-top: 16px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
}
</style>
